import * as Dialog from '@radix-ui/react-dialog'
import {
  BUDGET_CAPTURE_MAX_DISPLAY_HEIGHT_PX,
  BUDGET_CAPTURE_MAX_DISPLAY_WIDTH_PX,
  BUDGET_CAPTURE_MAX_PRINT_HEIGHT_MM,
} from '@constants'
import { usePreciosHubContext } from '@context/PreciosHubContext'
import { useAppStore } from '@store'
import atemporalLogoPresupuesto from '@assets/atemporal/AtemporalLogoPresupuesto.png'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import {
  buildArtisanCharacteristicsHtml,
  getBudgetArtisanCharacteristics,
} from '@utils/budget/artisanCharacteristics'
import {
  buildFabricCharacteristicsHtml,
  fetchFabricCareIconsAsDataUrls,
  resolveFabricCharacteristics,
  pickItemFabricTexture,
  resolveFabricPanelLabels,
  type FabricCareIconId,
} from '@utils/budget/fabricCharacteristics'
import { buildBudgetPiecesFromItems, formatBudgetPieceLine } from '@utils/budget/budgetPieces'
import { captureSceneForBudget } from '@utils/camera/captureSceneForBudget'
import { FabricCharacteristicsBlock } from '@components/Budget/FabricCharacteristicsBlock'
import { ui } from '@utils/i18n/uiCopy'
import { uiDateLocale } from '@utils/i18n/uiLocale'
import { useEffect, useMemo, useRef, useState } from 'react'

export function BudgetPreviewModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const items = useAppStore.use.items()
  const { resultadoPrecios } = usePreciosHubContext()
  const budgetContentRef = useRef<HTMLDivElement>(null)
  const [capturedSceneImage, setCapturedSceneImage] = useState('')

  const budgetPieces = useMemo(() => buildBudgetPiecesFromItems(items.values()), [items])

  const selectedParentLinkedItem = useMemo(() => {
    const selectedLinkedParent = Array.from(items.values()).find(
      (item: any) => item?.type === 'linkedItem' && item?.universal === false && item?.selected,
    ) as any
    if (selectedLinkedParent) return selectedLinkedParent
    return Array.from(items.values()).find(
      (item: any) => item?.type === 'linkedItem' && item?.universal === false,
    ) as any
  }, [items])

  const selectedFabricTexture = useMemo(
    () => pickItemFabricTexture(selectedParentLinkedItem),
    [selectedParentLinkedItem],
  )

  const fabricInfo = {
    acabado: String(selectedFabricTexture?.acabado || ''),
    name: String(selectedFabricTexture?.name || ''),
    imageUrl: String(selectedFabricTexture?.url || ''),
    miniUrl: String((selectedFabricTexture as any)?.urlMiniatura || ''),
  }

  const fabricLabels = useMemo(
    () => resolveFabricPanelLabels(selectedFabricTexture as any),
    [selectedFabricTexture],
  )
  const fabricCharacteristics = useMemo(
    () =>
      resolveFabricCharacteristics(
        fabricInfo.acabado,
        `${fabricLabels.subgroup || ''} ${fabricLabels.textureLabel || ''} ${fabricInfo.name}`.trim(),
      ),
    [fabricInfo.acabado, fabricInfo.name, fabricLabels.subgroup, fabricLabels.textureLabel],
  )
  const fabricMiniImageUrl = (() => {
    const zoneName = String((selectedFabricTexture as any)?.zoneName || '')
    const zoneMaterial = zoneName
      ? (selectedParentLinkedItem as any)?.zones?.[zoneName]?.material
      : null
    const miniName = String(zoneMaterial?.img || '').trim()
    const materialUrl = String(zoneMaterial?.url || fabricInfo.imageUrl || '').trim()
    if (!miniName || !materialUrl) return ''
    const lastSlash = materialUrl.lastIndexOf('/')
    if (lastSlash < 0) return ''
    const basePath = materialUrl.slice(0, lastSlash + 1)
    return `${basePath}${miniName}`
  })()
  const fabricSwatchSourceUrl = fabricInfo.miniUrl || fabricMiniImageUrl || fabricInfo.imageUrl
  const safeFabricImageUrl = fabricSwatchSourceUrl ? encodeURI(fabricSwatchSourceUrl) : ''
  const formattedPrecioTotal =
    resultadoPrecios && typeof resultadoPrecios.precioTotal === 'number'
      ? `${new Intl.NumberFormat(uiDateLocale()).format(resultadoPrecios.precioTotal)} €`
      : '-'

  const getLogoDataUrl = async () => {
    try {
      const response = await fetch(atemporalLogoPresupuesto)
      const blob = await response.blob()
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            resolve(atemporalLogoPresupuesto)
          }
        }
        reader.onerror = () => reject(new Error('No se pudo convertir logo'))
        reader.readAsDataURL(blob)
      })
    } catch {
      return atemporalLogoPresupuesto
    }
  }

  const getImageDataUrl = async (imageUrl: string) => {
    if (!imageUrl) return ''
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            resolve('')
          }
        }
        reader.onerror = () => reject(new Error('No se pudo convertir imagen'))
        reader.readAsDataURL(blob)
      })
    } catch {
      return ''
    }
  }

  const buildBudgetHtml = (
    logoSrc: string,
    fabricCareIconDataUrls: Partial<Record<FabricCareIconId, string>> = {},
  ) => {
    const today = new Date().toLocaleDateString(uiDateLocale())
    const escapeHtml = (value: string) =>
      value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')

    const imageBlock = capturedSceneImage
      ? `<img class="scene-capture" src="${capturedSceneImage}" alt="${escapeHtml(ui('currentScene'))}" />`
      : `<div style="height:240px;border:1px dashed #d8d8d8;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#6d6d6d;">${escapeHtml(ui('noCapture'))}</div>`
    const piecesHtml = budgetPieces
      .map((piece) => `<div class="line">${escapeHtml(ui('refPrefix'))} ${escapeHtml(formatBudgetPieceLine(piece))}</div>`)
      .join('')
    const fabricCareHtml = fabricCharacteristics
      ? buildFabricCharacteristicsHtml(escapeHtml, fabricCharacteristics, fabricCareIconDataUrls)
      : ''
    const tejidoHtml =
      fabricLabels.group || fabricLabels.subgroup || fabricLabels.textureLabel || safeFabricImageUrl
        ? `<div class="line"><span class="label-strong">${escapeHtml(ui('fabric'))}:</span></div>
    <div class="fabric-head">
        ${
          safeFabricImageUrl
            ? `<span class="fabric-swatch" style="background-image:url('${safeFabricImageUrl}');"></span>`
            : `<span class="fabric-dot"></span>`
        }
        <div>
          ${fabricLabels.group ? `<div class="fabric-title">${escapeHtml(fabricLabels.group)}</div>` : ''}
          ${fabricLabels.subgroup ? `<div class="fabric-subtitle">${escapeHtml(fabricLabels.subgroup)}</div>` : ''}
          ${fabricLabels.textureLabel ? `<div class="fabric-code">${escapeHtml(fabricLabels.textureLabel)}</div>` : ''}
        </div>
    </div>
    ${fabricCareHtml}`
        : `<div class="line">${escapeHtml(ui('fabric'))} <span class="muted">${escapeHtml(ui('withPhoto'))}</span></div>`

    return `<!doctype html>
<html lang="${uiDateLocale() === 'es-ES' ? 'es' : 'en'}">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(ui('budget'))}</title>
  <style>
    @page { size: A4; margin: 8mm; }
    @media print {
      html, body { margin: 0 !important; padding: 0 !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .sheet { page-break-inside: avoid; break-inside: avoid; }
      .preview, .scene-capture { page-break-inside: avoid; break-inside: avoid; }
      .logo { width: 180px !important; margin-bottom: 16px !important; }
      .preview { margin-top: 12px !important; }
      .scene-capture {
        max-width: 160mm !important;
        max-height: ${BUDGET_CAPTURE_MAX_PRINT_HEIGHT_MM}mm !important;
        width: auto !important;
        height: auto !important;
      }
      .budget-options-row { gap: 14px !important; }
      .budget-opt-card { max-width: 200px !important; min-width: 130px !important; flex: 1 1 150px !important; }
      .budget-opt-img-wrap { height: 140px !important; }
      .budget-opt-caption { font-size: 16px !important; margin-top: 8px !important; }
    }
    body { font-family: Arial, sans-serif; font-size: 16px; margin: 0; padding: 32px; color: #222; }
    .sheet { max-width: 840px; margin: 0 auto; }
    .logo { width: 220px; margin-bottom: 36px; }
    .line { margin-bottom: 14px; font-size: 16px; }
    .label-strong { font-weight: 700; }
    .muted { color: #8a8a8a; }
    .block { margin-top: 24px; }
    .total { margin-top: 22px; font-size: 16px; font-weight: 700; }
    .preview { margin-top: 20px; page-break-inside: avoid; break-inside: avoid; }
    .scene-capture {
      display: block;
      margin: 12px auto 0;
      max-width: min(${BUDGET_CAPTURE_MAX_DISPLAY_WIDTH_PX}px, 100%);
      max-height: ${BUDGET_CAPTURE_MAX_DISPLAY_HEIGHT_PX}px;
      width: auto;
      height: auto;
      object-fit: contain;
      border-radius: 12px;
      border: 1px solid #d8d8d8;
    }
    .fabric-head { display: flex; align-items: center; gap: 14px; margin: 4px 0 12px; }
    .fabric-dot { display: inline-block; width: 200px; height: 80px; border-radius: 10px; margin-top: 0; background: #b08f2f; flex-shrink: 0; border: 1px solid #d4d4d4; }
    .fabric-swatch { display: inline-block; width: 200px; height: 80px; border-radius: 10px; margin-top: 0; background-size: cover; background-position: center; flex-shrink: 0; border: 1px solid #d4d4d4; }
    .fabric-title { font-size: 16px; font-weight: 700; margin-bottom: 2px; }
    .fabric-subtitle { font-size: 14px; color: #555; margin-bottom: 1px; }
    .fabric-code { font-size: 13px; color: #777; }
    .budget-characteristics-block { margin-bottom: 8px; }
    .budget-options-wrap { margin: 4px 0 24px; }
    .budget-options-row { display: flex; flex-wrap: wrap; gap: 24px; align-items: stretch; justify-content: flex-start; }
    .budget-opt-card { flex: 1 1 220px; max-width: 300px; min-width: 180px; text-align: center; display: flex; flex-direction: column; align-items: center; }
    .budget-opt-img-wrap { width: 100%; height: 220px; display: block; position: relative; background: #f0f0f0; border-radius: 16px; border: 1px solid #cfcfcf; box-shadow: 0 6px 18px rgba(0,0,0,0.08); box-sizing: border-box; overflow: hidden; }
    .budget-opt-img-wrap img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
    .budget-opt-caption { margin-top: 14px; font-size: 16px; font-weight: 700; color: #1a1a1a; line-height: 1.35; }
    .characteristics-block { margin: 8px 0 20px; }
    .characteristics-text { margin: 0 0 4px; font-size: 16px; line-height: 1.35; color: #2a2a2a; }
    .fabric-care-block { margin: 10px 0 16px; }
    .fabric-care-title { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
    .fabric-care-group { font-size: 16px; font-weight: 700; margin: 4px 0 2px; color: #2a2a2a; }
    .fabric-care-desc { font-size: 16px; line-height: 1.45; color: #2a2a2a; margin-bottom: 8px; }
    .fabric-care-icons { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .fabric-care-icon { width: 28px; height: 28px; object-fit: contain; }
    .fabric-care-icon--slightly-small { width: 24px; height: 24px; }
    .fabric-care-icon--slightly-large { width: 32px; height: 32px; }
    .fabric-care-icon--medium { width: 36px; height: 36px; }
    .fabric-care-icon--large { width: 36px; height: 36px; }
  </style>
</head>
<body>
  <main class="sheet">
    <img class="logo" src="${logoSrc}" alt="Atemporal" />
    <div class="line"><span class="label-strong">${escapeHtml(ui('date'))}:</span> ${today}</div>
    <div class="line"><span class="label-strong">${escapeHtml(ui('model'))}:</span> Kube</div>
    ${buildArtisanCharacteristicsHtml(escapeHtml)}
    <div class="line"><span class="label-strong">${escapeHtml(ui('reference'))}:</span></div>
    ${piecesHtml}
    ${tejidoHtml}
    <div class="preview">${imageBlock}</div>
    <div class="total"><span class="label-strong">${escapeHtml(ui('total'))}</span> ${formattedPrecioTotal}</div>
  </main>
</body>
</html>`
  }

  const captureSceneWithOverlays = async () => {
    const storeBeforeCapture = useAppStore.getState()
    const previousHideDimensions = storeBeforeCapture.hideDimentions3D ?? false
    const previousCurrentLayer = storeBeforeCapture.currentLayer
    const previousCurrentObjectId = storeBeforeCapture.currentObjectID
    const previousSelected = Array.from(
      storeBeforeCapture.layers[previousCurrentLayer]?.selected || [],
    )
    const itemsForCapture = storeBeforeCapture.items

    useAppStore.setState((state) => {
      state.hideDimentions3D = true
      state.currentObjectID = ''
      state.layers[state.currentLayer]?.selected?.clear()
    })

    await new Promise((resolve) => requestAnimationFrame(resolve))
    await new Promise((resolve) => requestAnimationFrame(resolve))
    await new Promise((resolve) => setTimeout(resolve, 80))

    try {
      // Cámara dedicada cenital: no muta CameraControls ni la PerspectiveCamera de trabajo.
      const fullCaptureData = captureSceneForBudget(itemsForCapture)
      if (fullCaptureData.length > 100) {
        return fullCaptureData
      }
    } catch (error) {
      console.error('No se pudo capturar escena para presupuesto:', error)
    } finally {
      useAppStore.setState((state) => {
        state.hideDimentions3D = previousHideDimensions
        state.currentObjectID = previousCurrentObjectId
        if (state.layers[previousCurrentLayer]) {
          state.layers[previousCurrentLayer].selected.clear()
          previousSelected.forEach((id) => state.layers[previousCurrentLayer].selected.add(id))
        }
      })
    }
    return ''
  }

  useEffect(() => {
    if (!open) return
    const capture = async () => {
      const imageData = await captureSceneWithOverlays()
      setCapturedSceneImage(imageData)
    }
    capture()
  }, [open])

  const handleDownloadBudget = async () => {
    const target = budgetContentRef.current
    if (!target) return

    const cloneWrapper = document.createElement('div')
    cloneWrapper.style.position = 'fixed'
    cloneWrapper.style.left = '-100000px'
    cloneWrapper.style.top = '0'
    cloneWrapper.style.width = '1024px'
    cloneWrapper.style.background = '#ffffff'
    cloneWrapper.style.padding = '24px'
    cloneWrapper.style.zIndex = '-1'

    const clonedContent = target.cloneNode(true) as HTMLElement
    const clonedActions = clonedContent.querySelector('[data-budget-actions]') as HTMLElement | null
    if (clonedActions) clonedActions.style.display = 'none'
    const clonedFabricSwatchWrap = clonedContent.querySelector(
      '[data-fabric-swatch-wrap]',
    ) as HTMLElement | null
    if (clonedFabricSwatchWrap) clonedFabricSwatchWrap.style.display = 'none'

    const clonedFabricSwatchImg = clonedContent.querySelector(
      '[data-fabric-swatch-img]',
    ) as HTMLImageElement | null
    if (clonedFabricSwatchImg && safeFabricImageUrl) {
      const fabricDataUrl = await getImageDataUrl(safeFabricImageUrl)
      if (fabricDataUrl) {
        clonedFabricSwatchImg.src = fabricDataUrl
      }
    }

    const budgetOptionImgs = clonedContent.querySelectorAll(
      '[data-budget-option-row] img',
    ) as NodeListOf<HTMLImageElement>
    await Promise.all(
      Array.from(budgetOptionImgs).map(async (img) => {
        const src = img.getAttribute('src')
        if (!src || src.startsWith('data:')) return
        const dataUrl = await getImageDataUrl(src)
        if (dataUrl) img.src = dataUrl
      }),
    )

    cloneWrapper.appendChild(clonedContent)
    document.body.appendChild(cloneWrapper)

    const clonedImages = Array.from(cloneWrapper.querySelectorAll('img')) as HTMLImageElement[]
    await Promise.all(
      clonedImages.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve()
              return
            }
            img.onload = () => resolve()
            img.onerror = () => resolve()
          }),
      ),
    )

    const captureCanvas = await html2canvas(cloneWrapper, {
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      scale: Math.min(window.devicePixelRatio || 1, 2),
    })
    cloneWrapper.remove()

    const imgData = captureCanvas.toDataURL('image/png')
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 8
    const usableWidth = pageWidth - margin * 2
    const usableHeight = pageHeight - margin * 2
    const widthScale = usableWidth / captureCanvas.width
    const heightScale = usableHeight / captureCanvas.height
    const scale = Math.min(widthScale, heightScale)
    const renderWidth = captureCanvas.width * scale
    const renderHeight = captureCanvas.height * scale
    const offsetX = margin + (usableWidth - renderWidth) / 2
    const offsetY = margin

    pdf.addImage(imgData, 'PNG', offsetX, offsetY, renderWidth, renderHeight)
    pdf.save(`presupuesto-${new Date().toISOString().slice(0, 10)}.pdf`)
  }

  const handlePrintBudget = async () => {
    const logoSrc = await getLogoDataUrl()
    const fabricCareIcons = await fetchFabricCareIconsAsDataUrls(fabricCharacteristics)
    const html = buildBudgetHtml(logoSrc, fabricCareIcons)

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    document.body.appendChild(iframe)

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
      } finally {
        setTimeout(() => {
          iframe.remove()
        }, 1200)
      }
    }
    iframe.srcdoc = html
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <Dialog.Portal>
        <Dialog.Overlay className='fixed inset-0 z-[100] bg-black/70' />
        <Dialog.Content className='fixed left-1/2 top-1/2 z-[101] max-h-[90vh] w-[92vw] max-w-4xl -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-2xl bg-white p-4 shadow-2xl min-[768px]:p-6'>
          <Dialog.Title className='sr-only'>{ui('budget')}</Dialog.Title>
          <div
            ref={budgetContentRef}
            data-budget-root
            className='mx-auto max-w-3xl'
            style={{ fontFamily: 'Arial, sans-serif' }}
          >
            <img
              src={atemporalLogoPresupuesto}
              alt='Atemporal'
              className='mb-8 w-44'
            />
            <div
              data-budget-text
              className='space-y-2 text-[#2A2A2A]'
            >
              <p>
                <span className='font-bold'>{ui('date')}:</span> {new Date().toLocaleDateString(uiDateLocale())}
              </p>
              <p>
                <span className='font-bold'>{ui('model')}:</span> Kube
              </p>
              <div className='mt-2'>
                <p>
                  <span className='font-bold'>{ui('characteristics')}:</span>
                </p>
                <div className='mt-1 space-y-1 leading-snug'>
                  {getBudgetArtisanCharacteristics().map((text) => (
                    <p key={text}>{text}</p>
                  ))}
                </div>
              </div>
              <p>
                <span className='font-bold'>{ui('reference')}:</span>
              </p>
              {budgetPieces.map((piece) => (
                <p key={piece.id}>{ui('refPrefix')} {formatBudgetPieceLine(piece)}</p>
              ))}
              {(fabricLabels.group ||
                fabricLabels.subgroup ||
                fabricLabels.textureLabel ||
                safeFabricImageUrl) && (
                <div>
                  <p>
                    <span className='font-bold'>{ui('fabric')}:</span>
                  </p>
                  <div className='mb-2 mt-1 flex items-center gap-3.5'>
                    {safeFabricImageUrl ? (
                      <span
                        data-fabric-swatch-wrap
                        className='inline-block h-[80px] w-[200px] overflow-hidden rounded-[10px] border border-[#D4D4D4]'
                      >
                        <img
                          src={safeFabricImageUrl}
                          alt=''
                          data-fabric-swatch-img
                          className='h-full w-full object-cover'
                          style={{ imageRendering: 'auto' }}
                        />
                      </span>
                    ) : (
                      <span
                        data-fabric-swatch-wrap
                        className='inline-block h-[80px] w-[200px] rounded-[10px] border border-[#D4D4D4] bg-[#b08f2f]'
                      />
                    )}
                    <div className='pl-1'>
                      {fabricLabels.group ? (
                        <p className='font-semibold text-[#2A2A2A]'>{fabricLabels.group}</p>
                      ) : null}
                      {fabricLabels.subgroup ? (
                        <p className='text-[#555]'>{fabricLabels.subgroup}</p>
                      ) : null}
                      {fabricLabels.textureLabel ? (
                        <p className='text-[#777]'>{fabricLabels.textureLabel}</p>
                      ) : null}
                    </div>
                  </div>
                  {fabricCharacteristics ? (
                    <FabricCharacteristicsBlock profile={fabricCharacteristics} />
                  ) : null}
                </div>
              )}
            </div>
            <div className='mt-8'>
              {capturedSceneImage ? (
                <img
                  src={capturedSceneImage}
                  alt={ui('currentScene')}
                  className='mx-auto mt-2 h-auto max-h-[420px] w-auto max-w-[560px] rounded-xl border border-[#DCDCDC] object-contain'
                  style={{
                    maxWidth: BUDGET_CAPTURE_MAX_DISPLAY_WIDTH_PX,
                    maxHeight: BUDGET_CAPTURE_MAX_DISPLAY_HEIGHT_PX,
                  }}
                />
              ) : null}
            </div>
            <p className='mt-7 text-2xl font-semibold text-[#1f1f1f]'>
              {ui('total')} {formattedPrecioTotal}
            </p>
            <div
              data-budget-actions
              className='mt-8 flex flex-wrap justify-end gap-3'
            >
              <button
                onClick={handlePrintBudget}
                className='rounded-xl border border-colorAtemporal px-4 py-2 text-sm font-medium text-colorAtemporal transition-colors hover:bg-[#f5f5f5]'
              >
                {ui('print')}
              </button>
              <button
                onClick={handleDownloadBudget}
                className='rounded-xl bg-colorAtemporal px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90'
              >
                {ui('download')}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
